import {
  Component,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  DestroyRef,
  OnInit,
} from '@angular/core';
import { MenuAction } from '../../../types';
import { isObservable, NEVER, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SubmenuDirection } from '../../../models';

@Component({
  selector: 'app-dropdown-option',
  templateUrl: './dropdown-option.component.html',
  styleUrls: ['./dropdown-option.component.scss'],
  standalone: false,
})
export class DropdownOptionComponent implements OnChanges, OnInit {
  private readonly destroyRef = inject(DestroyRef);

  private readonly menuInitialization$ = new ReplaySubject<void>(1);

  @HostBinding('class')
  private get classes(): string {
    let classes = 'font-main text-base text-navy-90 flex items-center whitespace-nowrap';

    if (this.menuAction?.actAsNotAnAction) {
      classes += ' cursor-default';
    }

    if (!this.menuAction?.actAsNotAnAction && !this.disabled) {
      classes += ' cursor-pointer';
    }

    if (this.disabledBinding) {
      classes += ' opacity-50';
    }

    if (this.isComingSoon) {
      classes += ' pointer-events-none cursor-default opacity-100 text-opacity-50 text-navy-50';
    }

    if (this.menuAction?.classStyle) {
      classes += ` ${this.menuAction.classStyle}`;
    }

    return classes;
  }

  get isComingSoon(): boolean {
    return this.menuAction?.comingSoon;
  }

  private get disabledBinding(): boolean {
    if (this.disabled) {
      return true;
    }

    if (this.menuAction) {
      return this.menuAction.disabledCondition?.call(null, this.context) || this.menuAction.disabled;
    }

    return false;
  }

  @HostBinding('class.hover:bg-navy-40')
  get isEnabled(): boolean {
    const hasSubmenu = !!this.submenuDirection;

    return !this.disabledBinding && (hasSubmenu ? true : !this.menuAction?.actAsNotAnAction);
  }

  private isHiddenByListener: boolean;

  @HostBinding('class.hidden')
  private get hidden(): boolean {
    if (this.menuAction?.displayCondition) {
      if (isObservable(this.menuAction.displayCondition(this.context))) {
        return this.isHiddenByListener;
      }
      return this.menuAction.displayCondition && !this.menuAction.displayCondition(this.context);
    }

    return false;
  }

  get translationKey(): string {
    return this.menuAction?.translationKeyFactory?.call(null, this.context) || this.menuAction.translationKey;
  }

  @HostBinding('class.two-rows')
  get secondaryTranslationKey(): string {
    return this.menuAction?.secondaryTranslationKey;
  }

  get icon(): string {
    return this.menuAction?.iconFactory?.call(null, this.context) || this.menuAction.icon;
  }

  @HostBinding('class.disabled')
  @HostBinding('class.cursor-default')
  get isDisabled(): boolean {
    return this.disabledBinding;
  }

  get itemTranslationParam(): Observable<any> {
    const params = this.menuAction?.translationKeyParams;
    if (!params) {
      return NEVER;
    } else if (isObservable(params)) {
      return params;
    }
    return of(params);
  }

  get itemSecondaryTranslationParam$(): Observable<any> {
    const params = this.menuAction?.secondaryTranslationKeyParams;
    if (!params) {
      return NEVER;
    } else if (isObservable(params)) {
      return params;
    }
    return of(params);
  }

  @Input()
  menuAction: MenuAction;

  @Input()
  context: any;

  @Input()
  disabled: boolean;

  @HostBinding('class.bg-navy-30')
  @Input()
  selected: boolean;

  @Input()
  action: (context?: any) => any;

  @Input()
  submenuDirection: SubmenuDirection;

  @Input()
  shouldDisplayTooltipIfExists = true;

  ngOnInit(): void {
    this.menuInitialization$
      .pipe(
        switchMap(() => this.menuAction.displayCondition(this.context) as Observable<boolean>),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((isDisplayed) => {
        this.isHiddenByListener = !isDisplayed;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'menuAction' in changes &&
      this.menuAction?.displayCondition &&
      isObservable(this.menuAction.displayCondition(this.context))
    ) {
      this.menuInitialization$.next();
    }
  }

  @HostListener('click')
  private onClick(): void {
    if (this.isEnabled) {
      this.action?.call(null, this.context);
      this.menuAction?.iconFactory?.call(null, this.context);
      this.menuAction?.action?.call(null, this.context);
    }
  }
}
